import { HttpErrorResponse } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { MessageService } from "primeng/api";
import { SESSION_END_REDIRECT_URL } from "../constants/session.constants";
import { AuthStateService } from "./auth-state.service";

export interface HandleErrorOptions {
  /** Si true (solo aplica a 401): limpia sesión y redirige fuera de la app (p. ej. usuarios sin refresh). */
  redirectToGoogle?: boolean;
}

/**
 * Intenta leer un mensaje legible del cuerpo de error HTTP (string JSON, ApiResponse, ProblemDetails, etc.).
 */
export function extractBackendErrorDetail(error: unknown): string | undefined {
  if (!(error instanceof HttpErrorResponse)) {
    return undefined;
  }
  return extractApiErrorDetail(error.error);
}

/** Extrae el mensaje funcional devuelto por envelopes y ProblemDetails del backend. */
export function extractApiErrorDetail(body: unknown): string | undefined {
  if (body == null) return undefined;

  if (typeof body === "string") {
    const text = body.trim();
    if (!text) return undefined;

    try {
      return extractApiErrorDetail(JSON.parse(text)) ?? text;
    } catch {
      return text;
    }
  }

  if (typeof body !== "object") return undefined;

  const value = body as Record<string, unknown>;
  const message = readNonEmptyString(value["message"]);
  if (message) return message;

  const detail = readNonEmptyString(value["detail"]);
  if (detail) return detail;

  const errors = collectErrorMessages(value["errors"]);
  if (errors.length) return errors.join("\n");

  const nestedDataMessage = extractApiErrorDetail(value["data"]);
  if (nestedDataMessage) return nestedDataMessage;

  return readNonEmptyString(value["title"]);
}

function readNonEmptyString(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const text = value.trim();
  return text || undefined;
}

function collectErrorMessages(value: unknown): string[] {
  if (value == null) return [];

  const directMessage = readNonEmptyString(value);
  if (directMessage) return [directMessage];

  if (Array.isArray(value)) {
    return value.flatMap((item) => collectErrorMessages(item));
  }

  if (typeof value !== "object") return [];

  const error = value as Record<string, unknown>;
  const description =
    readNonEmptyString(error["description"])
    ?? readNonEmptyString(error["message"]);
  if (description) return [description];

  return Object.values(error).flatMap((item) => collectErrorMessages(item));
}

@Injectable({ providedIn: "root" })
export class HttpErrorsService {
  private router = inject(Router);
  private messageService = inject(MessageService);
  private authState = inject(AuthStateService);

  // Evita spam de toasts cuando fallan múltiples requests al expirar la sesión
  private handling401 = false;

  handleError(
    status: number,
    _errorMessage?: string,
    errorBodyMessage?: string,
    options?: HandleErrorOptions,
  ): void {
    switch (status) {
      // 🔒 Sesión expirada / no autorizada
      case 401: {
        if (this.handling401) return;
        this.handling401 = true;

        const redirectToGoogle = options?.redirectToGoogle === true;

        this.messageService.add({
          severity: "warn",
          summary: "Unauthorized access",
          detail: redirectToGoogle
            ? "Tu sesión ha expirado. Serás redirigido."
            : "Tu sesión ha expirado o no tienes acceso. Por favor, inicia sesión nuevamente.",
        });

        // Limpia la sesión centralizada (signals + sessionStorage + broadcast a otras pestañas)
        this.authState.beginLogout();
        this.authState.markSkipAuthRestore();
        this.authState.clearSession();

        if (redirectToGoogle) {
          window.location.assign(SESSION_END_REDIRECT_URL);
          setTimeout(() => (this.handling401 = false), 500);
          break;
        }

        // Evita navegar repetidamente a /login
        const alreadyOnLogin = this.router.url?.startsWith("/login");
        const nav = alreadyOnLogin
          ? Promise.resolve()
          : this.router.navigate(["/login"], { replaceUrl: true });

        nav.finally(() => {
          // La navegación es interna: la aplicación no se reinicia y, por tanto,
          // debemos retirar explícitamente el overlay de cierre de sesión.
          this.authState.endLogout();
          setTimeout(() => (this.handling401 = false), 500);
        });
        break;
      }

      // 🌐 Sin conexión o servidor inaccesible
      case 0:
        this.messageService.add({
          severity: "warn",
          summary: "No connection",
          detail:
            "No se pudo conectar con el servidor. Verifica tu conexión e inténtalo nuevamente.",
        });
        break;

      case 400:
        this.messageService.add({
          severity: "error",
          summary: "Bad request",
          detail: errorBodyMessage || "Los datos enviados no son válidos.",
        });
        break;

      case 403:
        this.messageService.add({
          severity: "error",
          summary: "Access denied",
          detail:
            errorBodyMessage?.trim() ||
            "No tienes permisos para realizar esta operación.",
        });
        break;

      case 404:
        this.messageService.add({
          severity: "info",
          summary: "Not found",
          detail:
            errorBodyMessage?.trim() ||
            "No se encontró el recurso solicitado.",
        });
        break;

      case 406:
        this.messageService.add({
          severity: "warn",
          summary: "Request not accepted",
          detail:
            errorBodyMessage?.trim() ||
            "La solicitud no puede procesarse con los datos enviados.",
        });
        break;

      case 500:
        this.messageService.add({
          severity: "error",
          summary: "Server error",
          detail:
            errorBodyMessage?.trim() ||
            "El servidor no pudo completar la operación. Intenta más tarde.",
        });
        break;

      default:
        this.messageService.add({
          severity: "error",
          summary: "Unexpected error",
          detail:
            errorBodyMessage?.trim() ||
            "No fue posible completar la solicitud. Intenta nuevamente.",
        });
        break;
    }
  }
}
